import { type VariantACopyKey, pickVariantACopyKey } from "../lib/adhd";
import { useLang } from "../lib/i18n";
import { type AdhdCopy, adhdCopy } from "../lib/i18n/adhd";

// Variant A · Cisza — "how much do I have left?"
// One big number, one supportive sentence, nothing else.
// (design handoff §"1 · Wariant A — Cisza")
//
// Body-only: the surrounding <main>, header (logo + pill), chooser and nav
// are owned by HomeMaybeADHD so the chooser stays mounted across variant
// switches (preventing the "klik nic nie robi" intermittent dead-clicks).

type Props = {
  // Remaining kcal for today. May be 0 (limit reached) or fresh-user default.
  kcalRemaining: number;
  // Whether any meal has been logged today (drives the empty state).
  hasData: boolean;
  // True once past the usual lunch hour — pivots the contextual sentence.
  afterLunch: boolean;
};

function subFor(key: VariantACopyKey, t: AdhdCopy): string {
  switch (key) {
    case "limitDone":
      return t.aSubLimitDone;
    case "littleLeft":
      return t.aSubLittleLeft;
    case "evening":
      return t.aSubEvening;
    case "bigWindow":
      return t.aSubBigWindow;
  }
}

export function HomeAdhdA({ kcalRemaining, hasData, afterLunch }: Props) {
  const { lang } = useLang();
  const t = adhdCopy[lang];

  // Fresh user (nothing logged today): show the welcoming empty state copy
  // instead of a contextual sentence (design handoff §"Empty/edge states").
  const sub = hasData
    ? subFor(pickVariantACopyKey(kcalRemaining, afterLunch), t)
    : t.aSubFreshStart;

  return (
    <div
      className="flex flex-col items-start"
      style={{ flex: 1, justifyContent: "center", gap: 14, marginTop: -20 }}
    >
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 11,
          color: "var(--muted)",
          fontWeight: 700,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
        }}
      >
        {t.aEyebrow}
      </div>
      <div
        className="tabular"
        style={{
          fontFamily: "var(--font-display)",
          fontWeight: 800,
          fontSize: 148,
          lineHeight: 0.82,
          letterSpacing: "-0.06em",
          color: "var(--ink)",
        }}
      >
        {kcalRemaining}
      </div>
      <div
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 12,
          color: "var(--accent)",
          fontWeight: 700,
          letterSpacing: "0.04em",
          marginTop: -4,
        }}
      >
        {t.aUnit}
      </div>
      <p
        style={{
          fontSize: 19,
          color: "var(--ink-2)",
          fontWeight: 500,
          lineHeight: 1.35,
          maxWidth: 300,
          marginTop: 10,
          textWrap: "pretty",
        }}
      >
        {sub}
        <span style={{ color: "var(--accent)" }}>.</span>
      </p>
    </div>
  );
}
